import React, { useState, useEffect, useMemo } from 'react'
import { Chip, Grid, IconButton, InputAdornment } from '@material-ui/core'
import { jt, t } from 'ttag'

import { ReactComponent as AddIcon } from '../../../assets/v3/icons/add.svg'
import { ReactComponent as StarFilledIcon } from '../../../assets/v2/icons/star-filled.svg'
import { ReactComponent as StarIcon } from '../../../assets/v2/icons/star.svg'
import { ReactComponent as RemoveIcon } from '../../../assets/v3/icons/remove.svg'

import { CellInfo, Column, RowInfo } from 'react-table'
import { Table } from '../../../components/Table/Table'
import { isRtl } from '../../../i18nInit'
import { scrollToTop } from '../../../common/helpers/scroll'
import { darkBlue, red } from '../../../styles/material-ui-theme-v2'
import YumInput from '../../../components/YumInput/YumInput'
import YumCountrySelect from '../../../components/YumCountrySelect/YumCountrySelect'
import YumSwitch from '../../../components/YumSwitch/YumSwitch'
import YumButtonGroup from '../../../components/YumButtonGroup/YumButtonGroup'
import YumCancelButton from '../../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../../components/YumSubmitButton/YumSubmitButton'
import YumDialog from '../../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../../components/YumDialogContent/YumDialogContent'
import YumDialogContentText from '../../../components/YumDialogContentText/YumDialogContentText'
import YumDialogActions from '../../../components/YumDialogActions/YumDialogActions'
import { Button } from '../../../components/Button/Button'
import {
  isCompanyCountryRequired,
  isCompanyDivisionRequired,
  isCompanyUserCreatedTeams,
} from '../../RegisterUser/RegisterUser'
import TeamSelectionUI from '../../../components/TeamSelectionUI/TeamSelectionUI'
import GroupSelectionUI from '../../../components/GroupSelectionUI/GroupSelectionUI'
import JoinCompanyWrapper from './JoinAnotherCompany/JoinAnotherCompanyWrapper'
import {
  CompanyCountriesDto,
  CompanyId,
  DeleteUserOwnedCompany$Request,
  DivisionDto,
  DivisionId,
  DivisionRow,
  JoinCompany$Request,
  LeaveCompany$Request,
  Role,
  SetUserCountry$Request,
  SetUserDivision$Request,
  UserCompanyDto,
  UserCompanyRolesDto,
  UserId,
  UserProfileDto,
  DivisionCategoryRow,
  DivisionCategoryId,
} from '../../../services/api.types'
import { yuMuuvPublicCompanyId } from '../Settings'
import { hasFeatureFlag } from '../../../common/helpers/featureFlags'
import { translateDivisionCategoryType } from '../../TeamManagement/TeamManagement'

interface CompaniesBlockProps {
  userDto: UserProfileDto
  userCompanies: UserCompanyDto[]
  countries: CompanyCountriesDto[]
  divisions: DivisionDto[]
  hasRole: (role: Role) => boolean
  hasRoleInCompany: (companyId: CompanyId, role: Role) => boolean
  userCompanyRoles?: UserCompanyRolesDto[]
  joinCompany: (payload: JoinCompany$Request) => void
  leaveCompany: (payload: LeaveCompany$Request) => void
  deleteUserOwnedCompany: (payload: DeleteUserOwnedCompany$Request) => void
  setUserDivision: (payload: SetUserDivision$Request) => void
  setUserCountry: (payload: SetUserCountry$Request) => void
  togglePublicStatus: () => void
  createTeamLoading?: boolean
  resetCreateTeamLoading?: () => void
  getUserCompanyCountries: () => void
  //getCompanyDivisions: () => void
  getDivisionCategories: () => void
  // Division categories and groups props
  divisionCategories?: DivisionCategoryRow[]
  //companyGroups?: { [categoryId: string]: DivisionRow[] }
  // Group selection handlers
  setUserCategoryDivision?: (payload: {
    userId: UserId
    companyId: CompanyId
    categoryId: DivisionCategoryId
    divisionId: DivisionId
    isAdminView?: boolean
  }) => void
}

/**
 * CompaniesBlock component
 *
 * Displays and manages company-related functionality in the Settings view:
 * - Shows a table of all user companies (excluding public challenges company)
 * - Allows users to join new companies with a sign-up code
 * - Enables users to leave companies or delete owned companies
 * - Manages team selection within companies
 * - Handles country selection for companies
 * - Controls public challenges toggle
 *
 * This component is intended to be wrapped by CompaniesWrapper
 * which provides it with Redux state and actions.
 */
const CompaniesBlock: React.FC<CompaniesBlockProps> = ({
  userDto,
  userCompanies,
  countries,
  divisions,
  hasRole,
  hasRoleInCompany,
  userCompanyRoles,
  joinCompany,
  leaveCompany,
  deleteUserOwnedCompany,
  setUserDivision,
  setUserCountry,
  togglePublicStatus,
  createTeamLoading,
  resetCreateTeamLoading,
  getUserCompanyCountries,
  //getCompanyDivisions,
  getDivisionCategories,
  divisionCategories = [],
  //companyGroups = {},
  setUserCategoryDivision,
}) => {
  // State
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false)
  const [leaveCompanyId, setLeaveCompanyId] = useState<CompanyId | null>(null)
  const [leaveCompanyName, setLeaveCompanyName] = useState('')
  const [isLeaveCompanyOwner, setIsLeaveCompanyOwner] = useState(false)
  const [publicChallengesJustClicked, setPublicChallengesJustClicked] = useState(false)
  /*const [companyDivisions, setCompanyDivisions] = useState(false)

  useEffect(() => {
    setCompanyDivisions(false)
  }, [divisions])*/

  // Clean up createTeamLoading flag when component unmounts
  useEffect(() => {
    if (resetCreateTeamLoading) {
      resetCreateTeamLoading()
    }
    return () => {
      // Reset the flag when component unmounts to prevent it from persisting in global state
      if (resetCreateTeamLoading) {
        resetCreateTeamLoading()
      }
    }
  }, [resetCreateTeamLoading])

  // Fetch data on component mount
  /*useEffect(() => {
    getUserCompanyCountries()
    getCompanyDivisions()
  }, [getUserCompanyCountries, getCompanyDivisions])*/

  // Fetch division categories for all user companies on mount
  useEffect(() => {
    // Get unique company IDs from userCompanies (excluding public company)
    const companyIds = userCompanies
      ?.filter(({ companyId }) => companyId !== yuMuuvPublicCompanyId)
      ?.map(({ companyId }) => companyId)
      ?.filter((companyId, index, array) => array.indexOf(companyId) === index) // Remove duplicates

    // Fetch division categories once for all companies
    if (companyIds && companyIds.length > 0) {
      getDivisionCategories()
    }
  }, [userCompanies]) // Only depend on userCompanies, not the function reference

  // Derived values
  const filteredCompanies = userCompanies?.filter(({ companyId }) => companyId !== yuMuuvPublicCompanyId)
  const onlyHasPublicCompany = userCompanies.length === 1 && filteredCompanies.length === 0
  const publicChallengesAllowed =
    userCompanies?.some(({ publicGoalsEnabled }) => publicGoalsEnabled) || onlyHasPublicCompany || false
  const publicChallengesEnabled = userCompanies?.find(({ companyId }) => companyId === yuMuuvPublicCompanyId)?.active

  const showRole = hasRole(Role.ADMIN) || filteredCompanies?.some(({ active }) => !active)
  const showDivision =
    divisions?.some(({ companyId }) => companyId !== yuMuuvPublicCompanyId) ||
    userCompanies?.some((uc) => uc.userCreatedTeams)
  const showCountry = (countries || [])
    .filter(({ companyId }) => companyId !== yuMuuvPublicCompanyId)
    .some(({ countries }) => !!countries.length)

  const showJoinAnother = !hasFeatureFlag(userCompanies, 'hideJoinAnother')

  // Handlers
  const handleOpenLeaveModal = (companyId: CompanyId, companyName: string, ownerUserId: UserId | null) => {
    setLeaveCompanyId(companyId)
    setLeaveCompanyName(companyName)
    setIsLeaveCompanyOwner(userDto.userId === ownerUserId)
    setIsLeaveModalOpen(true)
  }

  const handleCloseLeaveModal = () => {
    setIsLeaveModalOpen(false)
    setLeaveCompanyId(null)
    setLeaveCompanyName('')
  }

  const handleLeaveCompany = () => {
    if (leaveCompanyId) {
      leaveCompany({ companyId: leaveCompanyId })
      handleCloseLeaveModal()
      scrollToTop()
    }
  }

  const handleDeleteUserOwnedCompany = () => {
    if (leaveCompanyId) {
      deleteUserOwnedCompany({ companyId: leaveCompanyId })
      handleCloseLeaveModal()
      scrollToTop()
    }
  }

  const handlePublicStatusToggle = () => {
    togglePublicStatus()
    setPublicChallengesJustClicked(true)

    setTimeout(() => {
      setPublicChallengesJustClicked(false)
    }, 1000)
  }

  const handleDivisionChange =
    (companyId: CompanyId, userId: UserId) => (event: React.ChangeEvent<{ value: unknown }>) => {
      const { value } = event.target as { value: DivisionId }
      setUserDivision({ companyId, userId, divisionId: value || null })
    }

  const handleCountryChange =
    (companyId: CompanyId, userId: UserId) => (event: React.ChangeEvent<{ value: unknown }>) => {
      const { value } = event.target as { value: string }
      setUserCountry({ companyId, userId, country: value })
    }

  // Handler for group selection changes
  const handleCategoryGroupChange =
    (categoryId: DivisionCategoryId) => (event: React.ChangeEvent<{ value: unknown }>) => {
      const divisionId = event.target.value as DivisionId
      // Find the company ID for this category
      const category = divisionCategories.find((cat) => cat.id === categoryId)
      if (category && setUserCategoryDivision) {
        setUserCategoryDivision({
          userId: userDto.userId,
          companyId: category.companyId,
          categoryId,
          divisionId,
          isAdminView: false,
        })
      }
    }

  // Use useMemo for the columns to prevent unnecessary recalculations
  const columns = useMemo(() => {
    const useAutocompleteSelect = hasFeatureFlag(userCompanies, 'useAutocompleteSelect')
    const columns = []

    columns.push({
      Header: t`Name`,
      id: 'companyName',
      Cell: ({ original: { companyName } }: CellInfo) => <span>{companyName}</span>,
      accessor: 'companyName',
      style: { alignSelf: 'flex-start' },
      minWidth: 150,
      maxWidth: 300,
    })

    if (showRole) {
      columns.push({
        Header: t`Role`,
        id: 'role',
        Cell: ({ original: { companyId, ownerUserId, active } }: CellInfo) => {
          let role = t`User`
          let color = 'default'
          let variant = 'outlined'
          const hasActualAdminRole = userCompanyRoles?.some(
            (cr) => cr.companyId === companyId && cr.roles.some((r) => r === Role.ADMIN),
          )
          if (userDto.userId === ownerUserId) {
            role = t`Owner`
            color = 'primary'
            variant = 'default'
          } else if (hasActualAdminRole) {
            role = t`Admin`
            color = 'primary'
            variant = 'default'
          }
          const deStyle = {
            textTransform: 'uppercase',
            fontSize: 11,
            margin: 0,
          }
          // @ts-ignore
          return role ? (
            <>
              {/* @ts-ignore */}
              <Chip
                size="small"
                color={color}
                variant={variant}
                label={role}
                style={{
                  textTransform: 'uppercase',
                  fontSize: 11,
                  margin: isRtl ? '0 0 0 5px' : '0 5px 0 0',
                  backgroundColor: color === 'primary' ? darkBlue : 'inherit',
                  borderColor: color === 'primary' ? darkBlue : 'inherit',
                }}
              />
              {!active && (
                <>
                  {/* @ts-ignore */}
                  <Chip color="secondary" variant="default" label={t`Deactivated`} style={deStyle} />
                </>
              )}
            </>
          ) : (
            ''
          )
        },
        accessor: 'role',
        style: { alignSelf: 'flex-start', flexWrap: 'wrap', overflow: 'hidden' },
        minWidth: 135,
      })
    }

    if (showDivision) {
      columns.push({
        Header: t`Groups`,
        accessor: 'divisionName',
        Cell: ({
          original: { companyId, userId, divisionId, teamSelectionLocked, teamSwitchingDisabled, maxTeamSize },
        }: RowInfo) => {
          const companyDivisions = (divisions || []).filter((d) => d.companyId === companyId)
          const companyTeams = (divisions || []).filter((d) => d.categoryId === null)
          return (
            <div>
              <TeamSelectionUI
                companyId={companyId}
                userId={userId}
                divisionId={divisionId}
                divisions={companyTeams}
                teamSelectionLocked={teamSelectionLocked}
                teamSwitchingDisabled={teamSwitchingDisabled}
                maxTeamSize={maxTeamSize}
                useAutocompleteSelect={useAutocompleteSelect}
                onDivisionChange={handleDivisionChange}
                errorMessage={t`Please select a team`}
              />

              {/* Add GroupSelectionUI components for each division category */}
              {divisionCategories && divisionCategories.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  {divisionCategories
                    .filter((category) => category.companyId === companyId)
                    .map((category) => {
                      //const categoryGroups = companyGroups[category.id] || []
                      const categoryGroups = companyDivisions.filter((d) => d.categoryId === category.id)
                      // const categoryName = translateDivisionCategoryType(category, true)

                      // Get the selected group ID from userCompanies divisionIds
                      const userCompany = userCompanies.find((uc) => uc.companyId === companyId && uc.userId === userId)
                      let selectedGroupId: DivisionId | null = null

                      if (userCompany && userCompany.divisionIds) {
                        try {
                          const divisionIdsObj = JSON.parse(userCompany.divisionIds)
                          selectedGroupId = divisionIdsObj[category.id] || null
                        } catch (e) {
                          console.warn('Failed to parse divisionIds:', userCompany.divisionIds)
                        }
                      }

                      return (
                        <div key={category.id} style={{ marginBottom: '12px' }}>
                          <GroupSelectionUI
                            companyId={companyId}
                            userId={userId}
                            category={category}
                            groups={categoryGroups}
                            selectedGroupId={selectedGroupId}
                            onGroupChange={handleCategoryGroupChange}
                            showIcon={false}
                            useAutocompleteSelect={useAutocompleteSelect}
                            isDuringRegistration={false}
                          />
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          )
        },
        style: { alignSelf: 'flex-start' },
        width: 300,
      })
    }

    if (showCountry) {
      columns.push({
        Header: t`Country`,
        accessor: 'country',
        Cell: ({ original: { companyId, userId, country } }: CellInfo) => {
          const isCountryRequired = isCompanyCountryRequired(companyId)
          const companyCountries = (countries || []).find((c) => c.companyId === companyId) || { countries: [] }
          return companyCountries.countries.length ? (
            <YumCountrySelect
              label={t`Country`}
              countries={companyCountries.countries}
              value={country || ''}
              onChange={handleCountryChange(companyId, userId)}
              required={isCountryRequired && country}
              useAutocompleteSelect={useAutocompleteSelect}
              style={{
                marginBottom: 0,
              }}
            />
          ) : (
            ''
          )
        },
        style: { alignSelf: 'flex-start' },
        width: 250,
      })
    }

    if (userCompanies.length > 1) {
      columns.push({
        Cell: ({ original: { companyId, companyName, ownerUserId } }: CellInfo) => (
          <IconButton
            style={{ color: red }}
            aria-label="delete"
            onClick={() => handleOpenLeaveModal(companyId, companyName, ownerUserId)}
          >
            {ownerUserId === userDto.userId ? (
              <RemoveIcon
                style={{
                  width: 24,
                  height: 24,
                }}
              />
            ) : (
              <svg
                style={{ padding: 2 }}
                width={24}
                height={24}
                focusable="false"
                aria-hidden="true"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="m17 7-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"></path>
              </svg>
            )}
          </IconButton>
        ),
        sortable: false,
        width: 80,
        style: { alignSelf: 'flex-start' },
      })
    }

    return columns
  }, [
    userCompanies,
    showRole,
    showDivision,
    showCountry,
    divisions,
    countries,
    userDto.userId,
    hasRoleInCompany,
    handleDivisionChange,
    handleCountryChange,
    handleOpenLeaveModal,
    divisionCategories,
    //companyGroups,
    handleCategoryGroupChange,
  ])

  return (
    <section className="settings__section">
      <Grid container spacing={2} justifyContent="space-between" alignItems="baseline" style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={'auto'}>
          <h3 style={{ marginBottom: 0 }}>{t`Companies & communities`}</h3>
        </Grid>
        <Grid item xs={12} sm={'auto'}>
          {publicChallengesAllowed && (
            <>
              <span
                onClick={handlePublicStatusToggle}
                style={{ marginRight: isRtl ? 0 : 10, marginLeft: isRtl ? 10 : 0, cursor: 'pointer' }}
              >{t`Public challenges`}</span>
              <YumSwitch
                checked={publicChallengesEnabled}
                onChange={handlePublicStatusToggle}
                disabled={publicChallengesJustClicked}
              />
            </>
          )}
        </Grid>
      </Grid>

      <Table className="company-table" data={filteredCompanies} columns={columns} />

      {showJoinAnother && (
        <Grid container>
          <Grid item xs={12} sm={6} md={4}>
            <YumButtonGroup>
              <JoinCompanyWrapper showJoinAnother={showJoinAnother} />
            </YumButtonGroup>
          </Grid>
        </Grid>
      )}

      {/* Leave/Delete Company Modal */}
      <YumDialog open={isLeaveModalOpen}>
        <YumDialogTitle>{isLeaveCompanyOwner ? t`Delete community` : t`Leave`}</YumDialogTitle>
        <YumDialogContent>
          <YumDialogContentText>
            {(() => {
              const companyNameElement = <strong key="companyName">{leaveCompanyName}</strong>
              return isLeaveCompanyOwner
                ? jt`You are the owner of ${companyNameElement}. Are you sure you want to delete it for every member with all its challenges? This cannot be undone.`
                : jt`Are you sure you want to leave from ${companyNameElement}?`
            })()}
          </YumDialogContentText>
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={handleCloseLeaveModal} />
          <YumSubmitButton
            text={isLeaveCompanyOwner ? t`Delete` : t`Leave`}
            onClick={isLeaveCompanyOwner ? handleDeleteUserOwnedCompany : handleLeaveCompany}
            color="error"
          />
        </YumDialogActions>
      </YumDialog>
    </section>
  )
}

// Export a memoized version of the component to prevent unnecessary re-renders
export default React.memo(CompaniesBlock)
